// src/pages/Overview.jsx
import { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  RefreshCw,
  LayoutGrid, Settings2, X, Eye, EyeOff,
  Plus, Save, Pencil, Trash2, Check,
} from 'lucide-react';
import { syncAPI, dashboardTabsAPI, profilesAPI, nasAPI } from '../api';
import { Button, Alert } from '../components/UI';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';

import {
  ClockWidget, DateWidget, WeatherWidget, StickyNoteWidget,
} from '../widgets';

import Top10Widget from '../widgets/Top10Widget';
import UserByStatusWidget from '../widgets/UserByStatusWidget';
import UserByTenantWidget from '../widgets/UserByTenantWidget';
import NasByStatusWidget from '../widgets/NasByStatusWidget';
import RadiusServerHealthWidget from  '../widgets/RadiusServerHealthWidget';
import TrafficChartWidget from '../widgets/TrafficChartWidget';
import ConcurrentWidget from '../widgets/ConcurrentWidget';
import { OnlineCountWidget, OnlineByRealmWidget } from '../widgets/OnlineWidget';
import { LoginSummaryWidget, TrafficSummaryWidget } from '../widgets/SummaryWidgets';
import { LastCreatedUsersWidget, LastExpiredUsersWidget } from '../widgets/RecentUsersWidgets';
import { RecentChangeLogsWidget, RecentAuthLogsWidget, RecentSyncLogsWidget } from '../widgets/RecentLogsWidgets';

const ResponsiveGrid = WidthProvider(Responsive);

const COLS     = 12;   // kolom di breakpoint 'lg' — ini yang disimpan ke backend
const ROW_UNIT = 64;
const GAP      = 8;

// Breakpoint mengikuti lebar CONTAINER (bukan window), jadi otomatis ikut
// kalau sidebar dibuka/ditutup. Hanya 'lg' yang bisa diedit & disimpan;
// layout md/sm/xs diturunkan otomatis dari layout 'lg' (lihat deriveLayout).
const BREAKPOINTS = { lg: 960, md: 680, sm: 440, xs: 0 };
const GRID_COLS   = { lg: COLS, md: 8, sm: 6, xs: 1 };

const DEFAULT_WIDGETS = [
  { id: 'recent_change_logs',    visible: true,  x: 0, y: 0,  w: 8, h: 5 },
  { id: 'clock',                 visible: false, x: 0, y: 5,  w: 2, h: 3 },
  { id: 'date',                  visible: false, x: 2, y: 5,  w: 2, h: 3 },
  { id: 'weather',               visible: false, x: 4, y: 5,  w: 2, h: 3 },
  { id: 'sticky_note',           visible: false, x: 6, y: 5,  w: 2, h: 3 },
];

const WIDGET_META = {
  clock:           { label: 'Umum: Jam',                     minW: 2, minH: 3 },
  date:            { label: 'Umum: Tanggal',                 minW: 2, minH: 3 },
  weather:         { label: 'Umum: Cuaca',                   minW: 2, minH: 3 },
  sticky_note:     { label: 'Umum: Sticky Note',             minW: 2, minH: 3 },
  top10_username:  { label: 'Analytics: Top 10 Username',    minW: 3, minH: 5 },
  top10_nas:       { label: 'Analytics: Top 10 NAS Device',  minW: 3, minH: 5 },
  top10_realm:     { label: 'Analytics: Top 10 Realm',       minW: 3, minH: 5 },
  top_profile:     { label: 'Analytics: Top 10 Profile',     minW: 3, minH: 5 },
  top_radius_server: { label: 'Analytics: Top 10 Radius Server', minW: 3, minH: 5 },
  user_by_status:  { label: 'Analytics: User by Status',     minW: 3, minH: 4 },
  user_by_tenant:  { label: 'Analytics: User per Tenant',    minW: 3, minH: 5 },
  nas_by_status:   { label: 'Analytics: NAS by Status',      minW: 3, minH: 4 },
  radius_server_health: { label: 'Analytics: Radius Server Health', minW: 3, minH: 4 },
  online_count:    { label: 'Analytics: Online Sekarang',    minW: 2, minH: 3 },
  online_by_realm: { label: 'Analytics: Online per Realm',   minW: 3, minH: 5 },
  login_summary:   { label: 'Analytics: Login Auth Summary', minW: 3, minH: 4 },
  traffic_summary: { label: 'Analytics: Traffic Summary',    minW: 3, minH: 5 },
  traffic_chart:   { label: 'Analytics: Traffic Chart (Upload/Download)', minW: 4, minH: 5 },
  concurrent:      { label: 'Analytics: Concurrent Users (Histogram)',    minW: 4, minH: 5 },
  last_created_users:  { label: 'Activity: User Terbaru Dibuat',  minW: 3, minH: 5 },
  last_expired_users:  { label: 'Activity: User Expired Terbaru', minW: 3, minH: 5 },
  recent_change_logs:  { label: 'Activity: Audit Trail Terbaru',  minW: 4, minH: 5 },
  recent_auth_logs:    { label: 'Activity: Aktivitas Login Terbaru', minW: 4, minH: 5 },
  recent_sync_logs:    { label: 'Activity: Sync Log Terbaru',     minW: 4, minH: 5 },
};

// Widget yang mendukung filter tambahan (saat ini: filter per-tenant) lewat menu ⋯.
const FILTERABLE_WIDGETS = new Set([
  'top10_username', 'top10_nas', 'top10_realm', 'top_profile', 'top_radius_server',
  'user_by_status', 'nas_by_status',
  'online_count', 'online_by_realm',
  'login_summary', 'traffic_summary',
  'traffic_chart', 'concurrent',
  'last_created_users', 'last_expired_users',
  'recent_change_logs', 'recent_auth_logs', 'recent_sync_logs',
]);

// Widget yang punya pemilih periode (filter[period]) di pojok kanan atas.
const PERIOD_WIDGETS = new Set([
  'traffic_chart', 'concurrent',
  'traffic_summary', 'login_summary',
  'top10_realm', 'top10_username', 'top10_nas', 'top_profile', 'top_radius_server',
]);
const PERIOD_OPTIONS = [
  { value: '',      label: 'Default' },
  { value: 'today', label: 'Hari ini' },
  { value: '1h',    label: '1 jam' },
  { value: '24h',   label: '24 jam' },
  { value: '7d',    label: '7 hari' },
  { value: '30d',   label: '30 hari' },
  { value: 'month', label: 'Bulan ini' },
];

// Widget dengan chart yang bisa dipecah (grouped) berdasarkan dimensi tertentu.
// Catatan: 'concurrent' sekarang histogram time-series seperti 'traffic_chart' —
// group_by di sini DIKIRIM ke backend (bukan lagi murni client-side).
const GROUP_BY_OPTIONS = {
  traffic_chart: [
    { value: '',             label: 'Total' },
    { value: 'username',     label: 'Username' },
    { value: 'nasipaddress', label: 'NAS Device' },
    { value: 'realm',        label: 'Realm' },
    { value: 'server_name',  label: 'Server' },
    { value: 'groupname',    label: 'Profile' },
  ],
};
// Concurrent pakai persis opsi yang sama dengan traffic_chart.
GROUP_BY_OPTIONS.concurrent = GROUP_BY_OPTIONS.traffic_chart;

// ── Multi-instance helpers ────────────────────────────────────────────────────
// Widget id boleh berupa "tipe" (mis. "sticky_note") atau "tipe#n" (mis. "sticky_note#2")
// untuk mengizinkan beberapa instance dari widget yang sama berdampingan.
function baseId(id) {
  return String(id).split('#')[0];
}

// ── Widget registry helpers ───────────────────────────────────────────────────
function ensureAllWidgets(list) {
  const safeList = Array.isArray(list) ? list : [];
  const existingIds = new Set(safeList.map(w => baseId(w.id)));
  const missingIds = Object.keys(WIDGET_META).filter(id => !existingIds.has(id));
  if (missingIds.length === 0) return safeList;

  const maxY = safeList.reduce((m, w) => Math.max(m, (w.y ?? 0) + (w.h ?? 0)), 0);
  const missing = missingIds.map((id, i) => {
    const meta = WIDGET_META[id];
    return { id, visible: false, x: 0, y: maxY + i, w: meta.minW, h: meta.minH };
  });
  return [...safeList, ...missing];
}

function normalizeWidget(w) {
  const meta  = WIDGET_META[baseId(w.id)] || { minW: 1, minH: 2 };
  const width = Math.max(meta.minW, Math.min(COLS, w.w || meta.minW));
  return {
    ...w,
    w: width,
    h: Math.max(meta.minH, w.h || meta.minH),
    x: Math.max(0, Math.min(COLS - width, w.x ?? 0)),
    y: Math.max(0, w.y ?? 0),
  };
}

// Turunkan layout untuk breakpoint yang lebih sempit dari layout 'lg':
// urut sesuai posisi asli (y, lalu x), skala lebar, lalu susun mengalir kiri→kanan.
function deriveLayout(widgets, cols) {
  const sorted = [...widgets].sort((a, b) => (a.y - b.y) || (a.x - b.x));
  let curX = 0, rowY = 0, rowH = 0;
  return sorted.map(w => {
    const meta = WIDGET_META[baseId(w.id)] || { minW: 1, minH: 2 };
    const minW = Math.min(meta.minW, cols);
    let width = Math.max(minW, Math.min(cols, Math.round(w.w * cols / COLS)));
    // Kolom sedikit: snap ke setengah atau penuh supaya tidak ada sisa bolong
    if (cols <= 6 && cols > 1) width = width > cols / 2 ? cols : Math.max(minW, Math.ceil(cols / 2));
    if (curX + width > cols) { rowY += rowH; curX = 0; rowH = 0; }
    const item = { i: w.id, x: curX, y: rowY, w: width, h: w.h, minW, minH: meta.minH, maxW: cols };
    curX += width;
    rowH = Math.max(rowH, w.h);
    return item;
  });
}

// ── WidgetContent ─────────────────────────────────────────────────────────────
// Semua widget mengambil data sendiri via /dashboard/overview — tidak ada prop data/loading.
// `extra` adalah filter tambahan dari widget.filter (mis. { tenant_id }) yang di-set lewat menu ⋯.
// `userRole` dipakai untuk default filter khusus role 'user': default group_by = 'realm'
// (server tidak relevan buat role ini — opsinya juga disembunyikan di WidgetQuickMenu).
function WidgetContent({ id, extra = {}, userRole }) {
  const roleGroupByDefault = userRole === 'user' ? { group_by: 'realm' } : {};
  switch (baseId(id)) {
    case 'clock':           return <ClockWidget />;
    case 'date':            return <DateWidget />;
    case 'weather':         return <WeatherWidget />;
    case 'sticky_note':     return <StickyNoteWidget />;
    case 'top10_username':  return <Top10Widget widget="top10_username"  filter={{ limit: 10, ...extra }} />;
    case 'top10_nas':       return <Top10Widget widget="top10_nas"       filter={{ limit: 10, ...extra }} />;
    case 'top10_realm':     return <Top10Widget widget="top10_realm"     filter={{ limit: 10, ...extra }} />;
    case 'top_profile':     return <Top10Widget widget="top_profile"     filter={{ limit: 10, ...extra }} />;
    case 'top_radius_server': return <Top10Widget widget="top_radius_server" filter={{ limit: 10, ...extra }} />;
    case 'user_by_status':  return <UserByStatusWidget filter={{ ...extra }} />;
    case 'user_by_tenant':  return <UserByTenantWidget filter={{ limit: 10 }} />;
    case 'nas_by_status':   return <NasByStatusWidget filter={{ ...extra }} />;
    case 'radius_server_health': return <RadiusServerHealthWidget />;
    case 'traffic_chart':   return <TrafficChartWidget filter={{ limit: 5, ...roleGroupByDefault, ...extra }} />;
    case 'concurrent':      return <ConcurrentWidget filter={{ limit: 5, ...roleGroupByDefault, ...extra }} />;
    case 'online_count':    return <OnlineCountWidget filter={{ ...extra }} />;
    case 'online_by_realm': return <OnlineByRealmWidget filter={{ limit: 10, ...extra }} />;
    case 'login_summary':   return <LoginSummaryWidget filter={{ ...extra }} />;
    case 'traffic_summary': return <TrafficSummaryWidget filter={{ ...extra }} />;
    case 'last_created_users': return <LastCreatedUsersWidget filter={{ limit: 10, ...extra }} />;
    case 'last_expired_users': return <LastExpiredUsersWidget filter={{ limit: 10, ...extra }} />;
    case 'recent_change_logs': return <RecentChangeLogsWidget filter={{ limit: 10, ...extra }} />;
    case 'recent_auth_logs':   return <RecentAuthLogsWidget filter={{ limit: 10, ...extra }} />;
    case 'recent_sync_logs':   return <RecentSyncLogsWidget filter={{ limit: 10, ...extra }} />;
    default:                return null;
  }
}

// ── ManagePanel ───────────────────────────────────────────────────────────────
function ManagePanel({ widgets, onToggle, onAdd, onRemove, onReset, onClose }) {
  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 300, zIndex: 1000,
      background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border)',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Settings2 size={15} /> Daftar Widget
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
          <X size={16} />
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {Object.entries(WIDGET_META).map(([id, meta]) => {
          const instances = widgets.filter(w => baseId(w.id) === id);
          return (
            <div key={id} style={{ padding: '10px 12px', background: 'var(--bg-card)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>{meta.label}</span>
                <button onClick={() => onAdd(id)} title="Tambah widget ini (duplikat)"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-blue)', padding: 4, display: 'flex' }}>
                  <Plus size={14} />
                </button>
              </div>
              {instances.map((w, idx) => (
                <div key={w.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, paddingLeft: 6 }}>
                  <span style={{ fontSize: 11, color: w.visible ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                    {instances.length > 1 ? `Instance #${idx + 1}` : 'Tampil'}
                  </span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => onToggle(w.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: w.visible ? 'var(--accent-blue)' : 'var(--text-muted)', padding: 3, display: 'flex' }}>
                      {w.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                    </button>
                    {instances.length > 1 && (
                      <button onClick={() => onRemove(w.id)} title="Hapus instance ini"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-red)', padding: 3, display: 'flex' }}>
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
        <button onClick={onReset} style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)' }}>
          Reset Tab ke Default
        </button>
      </div>
    </div>
  );
}

// ── SaveConfirmModal ──────────────────────────────────────────────────────────
function SaveConfirmModal({ onSave, onDiscard, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '24px 28px', minWidth: 320, boxShadow: '0 16px 48px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>Simpan perubahan?</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
          Layout dan pengaturan widget pada tab ini akan disimpan ke server.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel}  style={{ padding: '7px 16px', background: 'var(--bg-input)',  border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Lanjut Edit</button>
          <button onClick={onDiscard} style={{ padding: '7px 16px', background: 'var(--bg-input)',  border: '1px solid var(--accent-red)',  borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13, color: 'var(--accent-red)' }}>Buang</button>
          <button onClick={onSave}    style={{ padding: '7px 16px', background: 'var(--accent-blue)', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600 }}>Simpan</button>
        </div>
      </div>
    </div>
  );
}

// ── SearchSelect ──────────────────────────────────────────────────────────────
// Komponen select dengan fitur pencarian (searchable dropdown).
// options: [{ value, label }]
function SearchSelect({ value, onChange, options, placeholder = 'Pilih…', disabled = false }) {
  const [open,   setOpen]   = useState(false);
  const [query,  setQuery]  = useState('');
  const ref    = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));
  const filtered = options.filter(o =>
    !query || o.label.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    if (!open) { setQuery(''); return; }
    inputRef.current?.focus();
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <button disabled={disabled} onClick={() => setOpen(v => !v)}
        style={{ width: '100%', padding: '5px 8px', fontSize: 12, background: disabled ? 'var(--bg-secondary)' : 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 4, color: selected ? 'var(--text-primary)' : 'var(--text-muted)', cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ fontSize: 9, color: 'var(--text-muted)', flexShrink: 0 }}>▼</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0, zIndex: 3000, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 4, boxShadow: '0 6px 20px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
          <div style={{ padding: '6px 6px 4px' }}>
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Cari…"
              style={{ width: '100%', padding: '4px 7px', fontSize: 12, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ maxHeight: 160, overflowY: 'auto' }}>
            {/* Opsi "reset / semua" */}
            <div onClick={() => { onChange(''); setOpen(false); }}
              style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text-muted)', background: !value ? 'var(--bg-input)' : 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
              onMouseLeave={e => e.currentTarget.style.background = !value ? 'var(--bg-input)' : 'transparent'}>
              — Semua —
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)' }}>Tidak ditemukan</div>
            )}
            {filtered.map(o => (
              <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
                style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text-primary)', background: String(o.value) === String(value) ? 'rgba(59,130,246,0.15)' : 'transparent', fontWeight: String(o.value) === String(value) ? 600 : 400 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
                onMouseLeave={e => e.currentTarget.style.background = String(o.value) === String(value) ? 'rgba(59,130,246,0.15)' : 'transparent'}>
                {o.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── PeriodPicker ──────────────────────────────────────────────────────────────
// Dropdown kecil di pojok kanan atas widget. Nilainya disimpan di widget.filter.period
// dan dikirim ke backend sebagai filter[period]. Kosong = default backend (tidak dikirim).
function PeriodPicker({ value, onChange, offsetRight }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      onMouseDown={e => e.stopPropagation()}
      title="Periode"
      style={{ position: 'absolute', top: 10, right: offsetRight, zIndex: 30, padding: '2px 6px', fontSize: 11, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-secondary)', outline: 'none', cursor: 'pointer', maxWidth: 110 }}>
      {PERIOD_OPTIONS.map(o => <option key={o.value || 'default'} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ── WidgetQuickMenu ───────────────────────────────────────────────────────────
function WidgetQuickMenu({ widgetId, filterable, tenants = [], currentFilter = {}, onFilterChange, onHide, userRole }) {
  const [open,     setOpen]     = useState(false);
  const [pos,      setPos]      = useState({ top: 0, left: 0 });
  const [profiles, setProfiles] = useState([]);
  const [nasList,  setNasList]  = useState([]);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  const canFilterTenant = filterable && userRole !== 'user';
  const supportsProfile = filterable && ['top10_username','user_by_status','last_created_users','last_expired_users','recent_auth_logs'].includes(baseId(widgetId));
  const supportsNas     = filterable && ['top10_nas','nas_by_status','online_count','online_by_realm','recent_auth_logs'].includes(baseId(widgetId));
  // Role 'user' tidak boleh lihat/pilih dimensi "Server", dan defaultnya di-set ke 'realm'.
  const rawGroupByOptions = GROUP_BY_OPTIONS[baseId(widgetId)];
  const groupByOptions  = rawGroupByOptions && userRole === 'user'
    ? rawGroupByOptions.filter(o => o.value !== 'server_name')
    : rawGroupByOptions;
  const groupByDefault  = userRole === 'user' ? 'realm' : '';
  const supportsGroupBy = filterable && !!groupByOptions;

  const hasAnyFilter = canFilterTenant || supportsProfile || supportsNas || supportsGroupBy;
  const selectedTenantId = currentFilter.tenant_id;

  // Fetch profiles & NAS setiap kali tenant berubah (dan widget support filter tersebut)
  useEffect(() => {
    if (!selectedTenantId || (!supportsProfile && !supportsNas)) {
      setProfiles([]);
      setNasList([]);
      return;
    }
    let cancelled = false;
    setLoadingDeps(true);
    Promise.all([
      supportsProfile ? profilesAPI.list(selectedTenantId, { per_page: 200 }).catch(() => []) : Promise.resolve([]),
      supportsNas     ? nasAPI.list(selectedTenantId, { per_page: 200 }).catch(() => [])     : Promise.resolve([]),
    ]).then(([profs, nas]) => {
      if (cancelled) return;
      setProfiles(Array.isArray(profs) ? profs : (profs.profiles || []));
      setNasList(Array.isArray(nas) ? nas : (nas.nas_devices || []));
    }).finally(() => { if (!cancelled) setLoadingDeps(false); });
    return () => { cancelled = true; };
  }, [selectedTenantId, supportsProfile, supportsNas]);

  const calcPos = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    // posisi dropdown: coba di kiri tombol, jangan sampai keluar layar
    const w = 236;
    const left = Math.min(r.right - w, window.innerWidth - w - 8);
    setPos({ top: r.bottom + 4, left: Math.max(8, left) });
  };

  useEffect(() => {
    if (!open) return;
    calcPos();
    const fn = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          btnRef.current  && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const setFilter = (key, val) => {
    const next = { ...currentFilter };
    if (val === '' || val === undefined) delete next[key];
    else next[key] = val;
    // Reset profile & NAS kalau tenant berubah
    if (key === 'tenant_id') { delete next.groupname; delete next.nas_id; }
    onFilterChange(next);
  };

  const hasActive = Object.keys(currentFilter).some(k => currentFilter[k] !== undefined && currentFilter[k] !== '');

  const tenantOptions  = tenants.map(t => ({ value: t.id, label: t.name }));
  const profileOptions = profiles.map(p => ({ value: p.groupname || p.name, label: p.name || p.groupname }));
  const nasOptions     = nasList.map(n => ({ value: n.id, label: `${n.shortname || n.nasname} (${n.nasname})` }));

  const dropdown = open && (
    <div ref={menuRef} style={{ position: 'fixed', top: pos.top, left: pos.left, width: 236, padding: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 4 }}>

      {/* Sembunyikan */}
      <button onClick={() => { onHide(); setOpen(false); }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', fontSize: 12, background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', borderRadius: 4, textAlign: 'left', width: '100%' }}>
        <EyeOff size={13} /> Sembunyikan widget
      </button>

      {/* Filter section */}
      {hasAnyFilter && (
        <>
          <div style={{ margin: '2px 0', borderTop: '1px solid var(--border)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 8px 4px' }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Filter</span>
            {hasActive && (
              <button onClick={() => onFilterChange({})}
                style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px', fontSize: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--accent-red)', borderRadius: 4, cursor: 'pointer', color: 'var(--accent-red)' }}>
                <X size={9} /> Reset
              </button>
            )}
          </div>

          {/* Tenant */}
          {canFilterTenant && (
            <div style={{ padding: '2px 8px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Tenant</div>
              <SearchSelect
                value={currentFilter.tenant_id ?? ''}
                onChange={val => setFilter('tenant_id', val)}
                options={tenantOptions}
                placeholder="Semua Tenant"
              />
            </div>
          )}

          {/* Profile — hanya muncul setelah tenant dipilih */}
          {supportsProfile && selectedTenantId && (
            <div style={{ padding: '2px 8px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Profile</div>
              <SearchSelect
                value={currentFilter.groupname ?? ''}
                onChange={val => setFilter('groupname', val)}
                options={profileOptions}
                placeholder={loadingDeps ? 'Memuat…' : 'Semua Profile'}
                disabled={loadingDeps}
              />
            </div>
          )}

          {/* NAS — hanya muncul setelah tenant dipilih */}
          {supportsNas && selectedTenantId && (
            <div style={{ padding: '2px 8px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>NAS Device</div>
              <SearchSelect
                value={currentFilter.nas_id ?? ''}
                onChange={val => setFilter('nas_id', val)}
                options={nasOptions}
                placeholder={loadingDeps ? 'Memuat…' : 'Semua NAS'}
                disabled={loadingDeps}
              />
            </div>
          )}

          {/* Group By — pecah chart per dimensi (top-N, limit 5) */}
          {supportsGroupBy && (
            <div style={{ padding: '2px 8px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Group By</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {groupByOptions.map(o => {
                  const active = (currentFilter.group_by ?? groupByDefault) === o.value;
                  return (
                    <button key={o.value || 'total'} onClick={() => setFilter('group_by', o.value)}
                      style={{ padding: '3px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer', border: '1px solid var(--border)', background: active ? 'var(--accent-blue)' : 'var(--bg-input)', color: active ? '#fff' : 'var(--text-secondary)', fontWeight: active ? 600 : 400 }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 30 }}>
      <button ref={btnRef} onClick={() => setOpen(v => !v)} title="Opsi widget"
        style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-card)', border: `1px solid ${hasActive ? 'var(--accent-blue)' : 'var(--border)'}`, borderRadius: 6, color: hasActive ? 'var(--accent-blue)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
        ⋯
      </button>
      {open && ReactDOM.createPortal(dropdown, document.body)}
    </div>
  );
}

// ── TabBar (draggable + delete confirm + dashboard list) ──────────────────────
function DashboardList({ tabs, activeId, onSelect, onClose, onToggleHide }) {
  return (
    <div style={{
      position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
      background: 'var(--bg-secondary)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      minWidth: 220, padding: 6,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '4px 10px 6px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        Pilih Dashboard
      </div>
      {tabs.map((tab, i) => {
        const isActive = tab.id === activeId;
        const isHidden = tab.hidden;
        return (
          <div key={tab.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6 }}>
            <div onClick={() => { if (!isHidden) { onSelect(tab.id); onClose(); } }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: isHidden ? 'default' : 'pointer', opacity: isHidden ? 0.4 : 1 }}
              onMouseEnter={e => { if (!isActive && !isHidden) e.currentTarget.style.background = 'var(--bg-input)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ width: 20, height: 20, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isActive ? 'var(--accent-blue)' : 'var(--border)', color: isActive ? '#fff' : 'var(--text-muted)', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
              <span style={{ flex: 1, fontSize: 13, color: isActive ? 'var(--accent-blue)' : 'var(--text-primary)', textDecoration: isHidden ? 'line-through' : 'none' }}>{tab.name}</span>
              {isActive && !isHidden && <Check size={13} color="var(--accent-blue)" />}
            </div>
            <button onClick={() => onToggleHide(tab.id)} title={isHidden ? 'Tampilkan tab' : 'Sembunyikan tab'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, color: isHidden ? 'var(--text-muted)' : 'var(--accent-blue)', display: 'flex', flexShrink: 0 }}>
              {isHidden ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function DeleteConfirmPopover({ tabName, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 24px', minWidth: 300, boxShadow: '0 12px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Hapus Tab?</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
          Tab <strong style={{ color: 'var(--text-primary)' }}>"{tabName}"</strong> dan semua konfigurasi widget-nya akan dihapus permanen.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '7px 16px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Batal</button>
          <button onClick={onConfirm} style={{ padding: '7px 16px', background: 'var(--accent-red)', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600 }}>Hapus</button>
        </div>
      </div>
    </div>
  );
}

function TabBar({ tabs, activeId, managing, onSelect, onAdd, onRename, onDelete, onReorder, onToggleHide }) {
  const [editingId,    setEditingId]    = useState(null);
  const [editName,     setEditName]     = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showList,     setShowList]     = useState(false);
  const [dragIdx,      setDragIdx]      = useState(null);
  const [overIdx,      setOverIdx]      = useState(null);
  const inputRef = useRef(null);
  const listRef  = useRef(null);

  useEffect(() => {
    if (!showList) return;
    const fn = (e) => { if (listRef.current && !listRef.current.contains(e.target)) setShowList(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [showList]);

  const startEdit = (tab, e) => {
    e.stopPropagation();
    setEditingId(tab.id);
    setEditName(tab.name);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const commitEdit = () => {
    if (editName.trim()) onRename(editingId, editName.trim());
    setEditingId(null);
  };

  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver  = (e, idx) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverIdx(idx); };
  const handleDrop      = (e, idx) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== idx) onReorder(dragIdx, idx); setDragIdx(null); setOverIdx(null); };
  const handleDragEnd   = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        <div ref={listRef} style={{ position: 'relative', marginRight: 4 }}>
          <button onClick={() => setShowList(v => !v)} title="Daftar Dashboard"
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', background: showList ? 'var(--bg-card)' : 'transparent', border: '1px solid var(--border)', borderBottom: showList ? '1px solid var(--bg-card)' : '1px solid var(--border)', borderRadius: '6px 6px 0 0', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, marginBottom: showList ? -1 : 0 }}>
            <LayoutGrid size={13} />
          </button>
          {showList && <DashboardList tabs={tabs} activeId={activeId} onSelect={onSelect} onClose={() => setShowList(false)} onToggleHide={onToggleHide} />}
        </div>

        {tabs.filter(t => !t.hidden).map((tab, idx) => {
          const isActive  = tab.id === activeId;
          const isDragged = dragIdx === idx;
          const isOver    = overIdx === idx && dragIdx !== idx;
          return (
            <div key={tab.id} draggable
              onDragStart={e => handleDragStart(e, idx)} onDragOver={e => handleDragOver(e, idx)}
              onDrop={e => handleDrop(e, idx)} onDragEnd={handleDragEnd}
              onClick={() => onSelect(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', cursor: 'pointer', borderRadius: '6px 6px 0 0',
                background: isActive ? 'var(--bg-card)' : 'transparent',
                border: isActive ? '1px solid var(--border)' : '1px solid transparent',
                borderBottom: isActive ? '1px solid var(--bg-card)' : '1px solid transparent',
                marginBottom: isActive ? -1 : 0,
                color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                fontSize: 13, fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s', position: 'relative',
                opacity: isDragged ? 0.4 : 1,
                outline: isOver ? '2px solid var(--accent-blue)' : 'none',
                outlineOffset: -2, userSelect: 'none',
              }}
            >
              {editingId === tab.id ? (
                <input ref={inputRef} value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingId(null); }}
                  onClick={e => e.stopPropagation()}
                  style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, width: Math.max(60, editName.length * 9) }}
                />
              ) : (
                <span>{tab.name}</span>
              )}
              {managing && isActive && (
                <div style={{ display: 'flex', gap: 2, marginLeft: 4 }}>
                  <button onClick={e => startEdit(tab, e)} title="Rename"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'flex' }}>
                    <Pencil size={11} />
                  </button>
                  {tabs.length > 1 && (
                    <button onClick={e => { e.stopPropagation(); setDeleteTarget(tab); }} title="Hapus tab"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--accent-red)', display: 'flex' }}>
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {managing && (
          <button onClick={onAdd} title="Tambah tab baru"
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'none', border: '1px dashed var(--border)', borderRadius: '6px 6px 0 0', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, marginBottom: -1 }}>
            <Plus size={12} /> Tab Baru
          </button>
        )}
      </div>

      {deleteTarget && (
        <DeleteConfirmPopover
          tabName={deleteTarget.name}
          onConfirm={() => { onDelete(deleteTarget.id); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}

// ── Main Overview ─────────────────────────────────────────────────────────────
export default function Overview() {
  // ── Tab state ──────────────────────────────────────────────────────────────
  const [tabs,            setTabs]            = useState([]);
  const [activeId,        setActiveId]        = useState(null);
  const [tabsLoading,     setTabsLoading]     = useState(true);
  const [managing,        setManaging]        = useState(false);
  const [showPanel,       setShowPanel]       = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [saved,           setSaved]           = useState(false);
  const [syncing,         setSyncing]         = useState(false);
  const [error,           setError]           = useState(null);
  const [bp,              setBp]              = useState('lg');

  // Snapshot layout saat mulai edit, untuk "Buang perubahan"
  const editSnapshotRef = useRef(null);

  const { tenants } = useTenant();
  const { user }    = useAuth();
  const userRole    = user?.role || 'user';

  const activeTab = tabs.find(t => t.id === activeId) || tabs[0];
  const widgets   = activeTab?.widgets || ensureAllWidgets(DEFAULT_WIDGETS);

  const setWidgets = useCallback((updater) => {
    setTabs(prev => prev.map(t =>
      t.id === activeId
        ? { ...t, widgets: typeof updater === 'function' ? updater(t.widgets) : updater }
        : t
    ));
  }, [activeId]);

  // ── Load tabs from backend ─────────────────────────────────────────────────
  const loadTabs = useCallback(async () => {
    setTabsLoading(true);
    try {
      const data = await dashboardTabsAPI.list();
      const fetched = Array.isArray(data) ? data : (data._array || []);
      if (fetched.length === 0) {
        const created = await dashboardTabsAPI.create({ name: 'Default', position: 0, widgets: ensureAllWidgets(DEFAULT_WIDGETS) });
        setTabs([{ ...created, widgets: ensureAllWidgets(created.widgets) }]);
        setActiveId(created.id);
      } else {
        const normalized = fetched
          .sort((a, b) => a.position - b.position)
          .map(t => ({ ...t, widgets: ensureAllWidgets(t.widgets) }));
        setTabs(normalized);
        setActiveId(prev => normalized.find(t => t.id === prev)?.id || normalized[0].id);
      }
    } catch {
      setTabs([{ id: '__local__', name: 'Default', hidden: false, widgets: ensureAllWidgets(DEFAULT_WIDGETS) }]);
      setActiveId('__local__');
    } finally {
      setTabsLoading(false);
    }
  }, []);

  useEffect(() => { loadTabs(); }, [loadTabs]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true);
    try { await syncAPI.run(); }
    catch (e) { setError(e.message); }
    finally   { setSyncing(false); }
  };

  const setWidgetFilter = useCallback((widgetId, newFilter) => {
    setWidgets(prev => prev.map(w => w.id === widgetId ? { ...w, filter: newFilter } : w));
  }, [setWidgets]);

  const startManaging = () => {
    // Simpan snapshot layout aktif supaya bisa di-discard
    editSnapshotRef.current = JSON.stringify(activeTab?.widgets);
    setManaging(true);
    setShowPanel(false);
  };

  const stopManaging = () => {
    // Tanya dulu: simpan / buang / lanjut?
    setShowSaveConfirm(true);
  };

  const handleDiscardEdit = () => {
    // Kembalikan ke snapshot sebelum edit
    if (editSnapshotRef.current) {
      try {
        const snap = JSON.parse(editSnapshotRef.current);
        setWidgets(() => snap);
      } catch (_) {}
    }
    setShowSaveConfirm(false);
    setManaging(false);
    setShowPanel(false);
  };

  const toggleWidget = (id) =>
    setWidgets(prev => {
      const exists = prev.some(w => w.id === id);
      if (!exists) {
        const meta = WIDGET_META[baseId(id)] || { minW: 1, minH: 2 };
        const maxY = prev.reduce((m, w) => Math.max(m, (w.y ?? 0) + (w.h ?? 0)), 0);
        return [...prev, { id, visible: true, x: 0, y: maxY, w: meta.minW, h: meta.minH }];
      }
      return prev.map(w => w.id === id ? { ...w, visible: !w.visible } : w);
    });

  // Tambah instance baru dari widget yang sama (mis. traffic_chart -> traffic_chart#2 -> #3, dst).
  const addInstance = (id) =>
    setWidgets(prev => {
      const meta = WIDGET_META[id] || { minW: 1, minH: 2 };
      const usedNums = prev
        .filter(w => baseId(w.id) === id)
        .map(w => {
          const suffix = w.id.split('#')[1];
          return suffix ? parseInt(suffix, 10) : 1;
        });
      let n = 1;
      while (usedNums.includes(n)) n++;
      const newId = n === 1 ? id : `${id}#${n}`;
      const maxY = prev.reduce((m, w) => Math.max(m, (w.y ?? 0) + (w.h ?? 0)), 0);
      return [...prev, { id: newId, visible: true, x: 0, y: maxY, w: meta.minW, h: meta.minH }];
    });

  // Hapus satu instance widget tertentu (hanya berlaku jika masih ada instance lain dari tipe yang sama).
  const removeInstance = (id) =>
    setWidgets(prev => prev.filter(w => w.id !== id));

  const resetLayout = () =>
    setWidgets(ensureAllWidgets(DEFAULT_WIDGETS).map(w => ({ ...w })));

  const handleLayoutChange = useCallback((newLayout) => {
    setWidgets(prev => {
      let changed = false;
      const next = prev.map(w => {
        const item = newLayout.find(l => l.i === w.id);
        if (!item) return w;
        if (w.x === item.x && w.y === item.y && w.w === item.w && w.h === item.h) return w;
        changed = true;
        return { ...w, x: item.x, y: item.y, w: item.w, h: item.h };
      });
      return changed ? next : prev;
    });
  }, [setWidgets]);

  // ── Tab handlers (backend) ─────────────────────────────────────────────────
  const addTab = async () => {
    try {
      const created = await dashboardTabsAPI.create({ name: `Dashboard ${tabs.length + 1}`, position: tabs.length, widgets: ensureAllWidgets(DEFAULT_WIDGETS) });
      setTabs(prev => [...prev, { ...created, widgets: ensureAllWidgets(created.widgets) }]);
      setActiveId(created.id);
    } catch (e) { setError(e.message); }
  };

  const renameTab = async (id, name) => {
    setTabs(prev => prev.map(t => t.id === id ? { ...t, name } : t));
    try { await dashboardTabsAPI.update(id, { name }); }
    catch (e) { setError(e.message); loadTabs(); }
  };

  const deleteTab = async (id) => {
    const next = tabs.filter(t => t.id !== id);
    setTabs(next);
    if (activeId === id) setActiveId(next.find(t => !t.hidden)?.id || next[0]?.id);
    try { await dashboardTabsAPI.delete(id); }
    catch (e) { setError(e.message); loadTabs(); }
  };

  const reorderTab = async (fromIdx, toIdx) => {
    const visible = tabs.filter(t => !t.hidden);
    const reordered = [...visible];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setTabs([...reordered, ...tabs.filter(t => t.hidden)]);
    try { await dashboardTabsAPI.reorder(reordered.map((t, i) => ({ id: t.id, position: i }))); }
    catch (e) { setError(e.message); loadTabs(); }
  };

  const toggleHideTab = async (id) => {
    const tab = tabs.find(t => t.id === id);
    if (!tab) return;
    const newHidden = !tab.hidden;
    setTabs(prev => {
      const updated = prev.map(t => t.id === id ? { ...t, hidden: newHidden } : t);
      if (id === activeId && newHidden) {
        const first = updated.find(t => !t.hidden);
        if (first) setActiveId(first.id);
      }
      return updated;
    });
    try { await dashboardTabsAPI.update(id, { hidden: newHidden }); }
    catch (e) { setError(e.message); loadTabs(); }
  };

  const handleSave = async () => {
    if (!activeTab) return;
    setSaved('saving');
    try {
      await dashboardTabsAPI.update(activeTab.id, { widgets: activeTab.widgets });
      setSaved('done');
      setShowSaveConfirm(false);
      setManaging(false);
      setShowPanel(false);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError(e.message); setSaved(false); }
  };

  // ── Memos ──────────────────────────────────────────────────────────────────
  const visibleWidgets = useMemo(() => widgets.filter(w => w.visible), [widgets]);

  const layouts = useMemo(() => {
    const lg = visibleWidgets.map(w => {
      const meta = WIDGET_META[baseId(w.id)] || { minW: 1, minH: 2 };
      return { i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, minW: meta.minW, minH: meta.minH, maxW: COLS };
    });
    return {
      lg,
      md: deriveLayout(visibleWidgets, GRID_COLS.md),
      sm: deriveLayout(visibleWidgets, GRID_COLS.sm),
      xs: deriveLayout(visibleWidgets, GRID_COLS.xs),
    };
  }, [visibleWidgets]);

  // Edit (drag/resize) hanya di layar lebar — layout kecil itu turunan otomatis,
  // jangan sampai tersimpan menimpa layout 'lg' di backend.
  const canEditLayout = managing && bp === 'lg';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="animate-in" style={{ paddingBottom: 32 }}>
      {tabsLoading && (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Memuat dashboard…
        </div>
      )}
      {!tabsLoading && (<>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>System Overview</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Multi-tenant RADIUS management</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={managing ? stopManaging : startManaging} style={{ gap: 6 }}>
            <LayoutGrid size={13} /> {managing ? 'Selesai Edit' : 'Edit Panel'}
          </Button>
          {managing && (
            <>
              <Button variant="ghost" onClick={() => setShowPanel(v => !v)} style={{ gap: 6 }}>
                <Settings2 size={13} /> Daftar Widget
              </Button>
              <Button variant="ghost" onClick={resetLayout} style={{ gap: 6 }}>
                <RefreshCw size={13} /> Reset Tab
              </Button>
              <Button variant={saved === 'done' ? 'secondary' : 'primary'} onClick={handleSave} loading={saved === 'saving'} style={{ gap: 6 }}>
                {saved === 'done' ? <Check size={13} /> : <Save size={13} />} {saved === 'done' ? 'Tersimpan!' : 'Simpan'}
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={handleSync} loading={syncing} size="sm">
            <RefreshCw size={13} /> Run Sync
          </Button>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}

      {/* Tab bar */}
      <TabBar
        tabs={tabs}
        activeId={activeId}
        managing={managing}
        onSelect={setActiveId}
        onAdd={addTab}
        onRename={renameTab}
        onDelete={deleteTab}
        onReorder={reorderTab}
        onToggleHide={toggleHideTab}
      />

      {managing && bp !== 'lg' && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 'var(--radius)', background: 'rgba(245,158,11,0.10)', border: '1px dashed #f59e0b', fontSize: 12, color: '#f59e0b', textAlign: 'center' }}>
          Layar terlalu sempit untuk mengatur tata letak. Lebarkan jendela (atau tutup sidebar) untuk drag &amp; resize widget.
        </div>
      )}

      {managing && bp === 'lg' && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 'var(--radius)', background: 'rgba(59,130,246,0.08)', border: '1px dashed var(--accent-blue)', fontSize: 12, color: 'var(--accent-blue)', textAlign: 'center' }}>
          Mode edit aktif — seret widget pakai handle di pojok kiri atas. Tarik pojok kanan-bawah untuk ubah ukuran. Klik <strong>Simpan</strong> untuk menyimpan perubahan.
        </div>
      )}

      {/* Grid */}
      <ResponsiveGrid
        className="layout"
        layouts={layouts}
        breakpoints={BREAKPOINTS}
        cols={GRID_COLS}
        rowHeight={ROW_UNIT}
        margin={[GAP, GAP]}
        containerPadding={[0, 0]}
        isDraggable={canEditLayout}
        isResizable={canEditLayout}
        draggableCancel="select,option,input"
        resizeHandles={['se']}
        compactType="vertical"
        preventCollision={false}
        useCSSTransforms
        onBreakpointChange={(next) => setBp(next)}
        // Simpan hanya dari interaksi user di 'lg' (bukan onLayoutChange, yang juga
        // menyala saat breakpoint berganti dan akan menimpa layout desktop).
        onDragStop={(l) => canEditLayout && handleLayoutChange(l)}
        onResizeStop={(l) => canEditLayout && handleLayoutChange(l)}
      >
        {visibleWidgets.map(w => (
          <div key={w.id} style={{ borderRadius: 'var(--radius-lg)' }}>
            {managing && (
              <>
                <div style={{ position: 'absolute', inset: 0, zIndex: 5, borderRadius: 'var(--radius-lg)', border: '1px dashed var(--accent-blue)', pointerEvents: 'none' }} />
                <WidgetQuickMenu
                  widgetId={w.id}
                  filterable={FILTERABLE_WIDGETS.has(baseId(w.id))}
                  tenants={tenants}
                  currentFilter={w.filter || {}}
                  onFilterChange={(f) => setWidgetFilter(w.id, f)}
                  onHide={() => toggleWidget(w.id)}
                  userRole={userRole}
                />
              </>
            )}
            {PERIOD_WIDGETS.has(baseId(w.id)) && (
              <PeriodPicker
                value={w.filter?.period}
                offsetRight={managing ? 38 : 10}
                onChange={(period) => {
                  const next = { ...(w.filter || {}) };
                  if (period) next.period = period; else delete next.period;
                  setWidgetFilter(w.id, next);
                }}
              />
            )}
            <div style={{ height: '100%' }}>
              <WidgetContent id={w.id} extra={w.filter || {}} userRole={userRole} />
            </div>
          </div>
        ))}
      </ResponsiveGrid>

      {visibleWidgets.length === 0 && (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Semua widget disembunyikan. Klik <strong>Edit Panel → Daftar Widget</strong> untuk menampilkan kembali.
        </div>
      )}

      {showPanel && (
        <ManagePanel widgets={widgets} onToggle={toggleWidget} onAdd={addInstance} onRemove={removeInstance} onReset={resetLayout} onClose={() => setShowPanel(false)} />
      )}

      {showSaveConfirm && (
        <SaveConfirmModal
          onSave={handleSave}
          onDiscard={handleDiscardEdit}
          onCancel={() => setShowSaveConfirm(false)}
        />
      )}

      <style>{`
        .react-resizable-handle { z-index: 20; }
        .react-grid-item.react-grid-placeholder {
          background: var(--accent-blue) !important;
          opacity: 0.15 !important;
          border-radius: var(--radius-lg);
        }
      `}</style>
      </>)}
    </div>
  );
}